import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchCommitment, createCommitment, updateCommitment } from './api';

const CommitmentForm = ({ isEdit = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    commitment_name: '',
    // commitment_name: '',
    commitment_description: '',
    note: '',
    number_of_calls: '',
    one_thirty_min: '',
    commitment_status: 'Pending',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isEdit && id) {
      const getCommitment = async () => {
        try {
          setIsLoading(true);
          const response = await fetchCommitment(id);
          setFormData(response.data);
          setError(null);
        } catch (error) {
          console.error('Error fetching commitment:', error);
          setError('Failed to load commitment details');
        } finally {
          setIsLoading(false);
        }
      };

      getCommitment();
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.commitment_name.trim()) {
      setError('Commitment name is required');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      if (isEdit) {
        await updateCommitment(id, formData);
      } else {
        await createCommitment(formData);
      }

      navigate('/CommitmentList');
    } catch (error) {
      console.error('Error saving commitment:', error);
      setError('Failed to save commitment. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto p-6 bg-white shadow-md rounded-lg mt-10">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">
        {isEdit ? 'Edit Commitment' : 'Create Commitment'}
      </h2>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg mb-6">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="commitment_name"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Commitment Name
          </label>
          <input
            type="text"
            id="commitment_name"
            name="commitment_name"
            value={formData.commitment_name}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter commitment name"
          />
        </div>

        <div>
          <label
            htmlFor="commitment_description"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Description
          </label>
          <textarea
            id="commitment_description"
            name="commitment_description"
            value={formData.commitment_description}
            onChange={handleChange}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Describe your commitment (optional)"
          />
        </div>

        <div>
          <label
            htmlFor="note"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Additional Notes
          </label>
          <textarea
            id="note"
            name="note"
            value={formData.note}
            onChange={handleChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Add any additional notes (optional)"
          />
        </div>

        <div>
          <label
            htmlFor="number_of_calls"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Number of Calls
          </label>
          <input
            type="number"
            id="number_of_calls"
            name="number_of_calls"
            value={formData.number_of_calls}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="No. of calls"
          />
        </div>

        <div>
          <label
            htmlFor="one_thirty_min"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            1:30 min
          </label>
          <input
            type="number"
            id="one_thirty_min"
            name="one_thirty_min"
            value={formData.one_thirty_min}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Commitment value for 1:30 min"
          />
        </div>

        <div className="flex flex-col space-y-1 pt-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <div className="flex bg-gray-100 p-1 rounded-lg">
            <button
              type="button"
              onClick={() => handleChange({ target: { name: 'commitment_status', value: 'Completed' } })}
              className={`flex-1 flex items-center justify-center py-2 px-4 text-sm font-medium rounded-md transition-all duration-200 ${formData.commitment_status === 'Completed'
                ? 'bg-white text-green-700 shadow-sm border border-gray-200'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50'
                }`}
            >
              {formData.commitment_status === 'Completed' && (
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
              )}
              Completed
            </button>
            <button
              type="button"
              onClick={() => handleChange({ target: { name: 'commitment_status', value: 'Not Completed' } })}
              className={`flex-1 flex items-center justify-center py-2 px-4 text-sm font-medium rounded-md transition-all duration-200 ${formData.commitment_status === 'Not Completed'
                ? 'bg-white text-red-700 shadow-sm border border-gray-200'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50'
                }`}
            >
              {formData.commitment_status === 'Not Completed' && (
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              )}
              Not Completed
            </button>
          </div>
        </div>

        <div className="pt-4">
          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-white font-medium 
              ${isLoading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              }`}
          >
            {isLoading
              ? (isEdit ? 'Updating...' : 'Creating...')
              : (isEdit ? 'Update Commitment' : 'Create Commitment')
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default CommitmentForm;